import {
  AVPreviewModal,
  AvDenovoPreviewModal,
  toConceptGroupOptions,
} from '@verantos-dev/feature-analytical-variables';
import type { AvKind, StatusChipConfig } from '@verantos-dev/feature-analytical-variables';
import type { AnalyticalVariable } from '@verantos-dev/types-cohorts';

interface AVPreviewModalRendererProps {
  id: string;
  isOpen: boolean;
  analyticalVariable: AnalyticalVariable | undefined;
  /**
   * Library AV UUID — used to fetch detail for the av_denovo branch.
   * av_ir rows render entirely from the IR carried in
   * ``analyticalVariable.jsonValue``, so this is optional for that
   * path. The criterion card always has the id on hand via
   * ``row.libraryItem`` / ``row.criterion``.
   */
  analyticalVariableId?: string;
  /**
   * Library AV kind hint. When ``av_denovo``, the renderer routes to
   * :class:`AvDenovoPreviewModal` which wraps :class:`AvContentCard`
   * trimmed to Description + SQL. When ``av_ir`` (or absent), the
   * legacy :class:`AVPreviewModal` wraps :class:`AnalyticalVariableCard`.
   */
  libraryKind?: AvKind;
  /**
   * Href to the standalone AV detail page. Surfaced as a "View full"
   * link in the av_denovo modal footer so users can jump to the full
   * tabset (Verification / Concepts / Decisions) the compact modal
   * intentionally hides. Should point at the cohort-scoped variable
   * view route when the modal was opened from inside a cohort — the
   * destination's breadcrumb hook picks up the cohort context from
   * the URL itself.
   */
  viewFullHref?: string;
  conceptGroups:
    | Array<{ conceptGroupId: string; conceptGroupName: string; owner?: string }>
    | undefined;
  statusChips?: StatusChipConfig[];
  onClose: () => void;
  onEditVariable?: () => void;
  actionsDisabled?: boolean;
  actionTooltip?: string;
  owner?: string;
  lastModified?: string;
  hasError?: boolean;
  errorTooltip?: string;
  hasWarning?: boolean;
  warningTooltip?: string;
}

export const AVPreviewModalRenderer = ({
  id,
  isOpen,
  analyticalVariable,
  analyticalVariableId,
  libraryKind,
  viewFullHref,
  conceptGroups,
  statusChips,
  onClose,
  onEditVariable,
  actionsDisabled,
  actionTooltip,
  owner,
  lastModified,
  hasError,
  errorTooltip,
  hasWarning,
  warningTooltip,
}: AVPreviewModalRendererProps) => {
  if (!isOpen) return null;

  // av_denovo branch — library SQL-first AVs have no DSL ``jsonValue``;
  // ``analyticalVariable`` is a thin stub from ``useCriteriaSection``.
  // Route to the compact AvContentCard modal which fetches the full
  // manifest and renders Description + SQL.
  if (libraryKind === 'av_denovo' && analyticalVariableId) {
    return (
      <AvDenovoPreviewModal
        id={id}
        analyticalVariableId={analyticalVariableId}
        isOpen={isOpen}
        onClose={onClose}
        viewFullHref={viewFullHref}
      />
    );
  }

  if (!analyticalVariable) return null;

  return (
    <AVPreviewModal
      id={id}
      analyticalVariable={analyticalVariable}
      conceptGroups={toConceptGroupOptions(conceptGroups)}
      statusChips={statusChips}
      onClose={onClose}
      onEditVariable={onEditVariable}
      actionsDisabled={actionsDisabled}
      actionTooltip={actionTooltip}
      owner={owner}
      lastModified={lastModified}
      hasError={hasError}
      errorTooltip={errorTooltip}
      hasWarning={hasWarning}
      warningTooltip={warningTooltip}
    />
  );
};
